import * as yup from "yup";

import { CustomerAccessData } from "./customer-model";
import { PhoneNumberUtil } from "google-libphonenumber";
import { useFormik } from "formik";
import { useTranslation } from "next-i18next";

const phoneUtil = PhoneNumberUtil.getInstance();

const isPhoneValid = (phone: string, phoneIso: string) => {
  try {
    const number = phoneUtil.parseAndKeepRawInput(phone, phoneIso);
    return phoneUtil.isValidNumber(number);
  } catch (error) {
    return false;
  }
};

const validateRestrictedEmails = (
  email: string,
  restrictedEmails: string[]
) => {
  try {
    const index = email.lastIndexOf("@");
    if (index > -1 && restrictedEmails.includes(email.slice(index))) {
      return false;
    }
    return true;
  } catch (error) {
    return true;
  }
};

/**
 * Manages form data for customer data step.
 * @param onNext a call back that signals submission
 * @returns a formik instance
 */
export function useCustomerAccessFormik(
  onNext: (data: CustomerAccessData) => void
) {
  const { t } = useTranslation();
  const messageRequired = t("common:common.required");
  const invalidDomain = t("common:common.invalid_domain");
  const messageTextOnly = t("common:common.text_only");
  const messageNumbersOnly = t("common:common.numbers_only");
  const messageEmployeeEmail = t(
    "request_access:customer_access.customer_email_error"
  );

  const expressionTextOnly = /^([^0-9@&*"']*)$/;
  const expressionCustomerEmail =
    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i;

  const validationSchema = yup.object().shape({
    firstName: yup
      .string()
      .required(messageRequired)
      .matches(expressionTextOnly, messageTextOnly),
    lastName: yup
      .string()
      .required(messageRequired)
      .matches(expressionTextOnly, messageTextOnly),
    role: yup
      .string()
      .required(messageRequired)
      .matches(expressionTextOnly, messageTextOnly),
    email: yup
      .string()
      .test("test", invalidDomain, function (value) {
        const { restrictedEmailDomains } = this.parent;
        return validateRestrictedEmails(value + "", restrictedEmailDomains);
      })
      .required(messageRequired)
      .matches(expressionCustomerEmail, messageEmployeeEmail),
    phoneIso: yup.string(),
    restrictedEmailDomains: yup.array().of(yup.string()),
    phoneNumber: yup
      .number()
      .test("test", messageRequired, function (value) {
        const { phoneIso } = this.parent;
        return isPhoneValid(value + "", phoneIso + "");
      })
      .required(messageRequired)
      .typeError(messageNumbersOnly),
  });

  const formik = useFormik<CustomerAccessData>({
    initialValues: {
      firstName: "",
      lastName: "",
      role: "",
      email: "",
      restrictedEmailDomains: [],
      phoneNumber: "",
      phoneIso: "",
    },
    validationSchema: validationSchema,
    // use handleSubmission instead
    onSubmit: (values) => {
      onNext(values);
    },
  });

  return formik;
}
